"use client";
import { useQuery, useMutation } from "react-query";
import { useCallback, useMemo } from "react";
import { DataTable, ColumnDef } from "@/components/ui/data-table";
import { OverallResponseItem } from "@/interfaces/overall-response.interface";
import useOverallResponseService from "@/services/overall-response-service";
import AdminHeader from "@/components/header/admin-header";
import { ADMIN_PANEL_HEADER } from "@/constants/admin.constant";
import { Rating } from "@/components/ui/rating";
import { OverallResponseChart } from "@/components/overall-response/overall-response-chart";
import { RotateCw, RotateCcw, Loader2, Info } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { formatDate } from "@/utils/date.utils";


/**
 * OverallResponseWrapper component is the main wrapper for the overall response
 * metrics page. Manages response data fetching, revalidation, and displays
 * response items in a table with chart visualization. Shows rating metrics
 * and refresh functionality.
 * 
 * @component
 * @returns {JSX.Element} Overall response page with table and chart
 * 
 * @example
 * ```tsx
 * <OverallResponseWrapper />
 * ```
 */
export const OverallResponseWrapper = () => {
  const { getOverallResponse, revalidateOverallResponse } = useOverallResponseService();

  const {
    data: responseData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ["overallResponse"],
    () => getOverallResponse(),
    {
      enabled: true,
      refetchOnMount: true,
    }
  );

  const revalidateMutation = useMutation(
    () => revalidateOverallResponse(),
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        console.error("Revalidation failed:", error);
      },
    }
  );

  const data: OverallResponseItem[] = responseData?.data || [];

  const analysing: boolean = responseData?.analysing || false;

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleReAnalyse = useCallback(() => {
    revalidateMutation.mutate();
  }, [revalidateMutation]);


  const columns: ColumnDef<OverallResponseItem>[] = useMemo(
    () => [
      {
        id: "corpus",
        header: "Corpus",
        accessor: (row) => row.corpus,
        render: (corpus) => <span className="font-medium capitalize">{corpus}</span>,
        sortable: false,
        headerClassName: "min-w-[150px]",
      },
      {
        id: "model",
        header: "Model",
        accessor: (row) => row.model,
        render: (model) => <span className="font-medium uppercase">{model}</span>,
        sortable: false,
        headerClassName: "min-w-[100px]",
      },
      {
        id: "clarity",
        header: "Clarity",
        accessor: (row) => row.clarity,
        render: (value) => <Rating value={value} />,
        sortable: false,
        headerClassName: "min-w-[140px]",
      },
      {
        id: "factuality",
        header: "Answer Relevancy",
        accessor: (row) => row["a relevancy"],
        render: (value) => <Rating value={value} />,
        sortable: false,
        headerClassName: "min-w-[140px]",
      },
      {
        id: "relevance",
        header: "Context Relevancy",
        accessor: (row) => row["c relevancy"],
        render: (value) => <Rating value={value} />,
        sortable: false,
        headerClassName: "min-w-[140px]",
      },
      {
        id: "faithfulness",
        header: "Faithfulness",
        accessor: (row) => row.faithfulness,
        render: (value) => <Rating value={value} />,
        sortable: false,
        headerClassName: "min-w-[140px]",
      },
      {
        id: "coherence",
        header: "Coherence",
        accessor: (row) => row.coherence,
        render: (value) => <Rating value={value} />,
        sortable: false,
        headerClassName: "min-w-[140px]",
      },
    ],
    []
  );

  return (
    <div className="h-full flex flex-col bg-background text-text-500 px-4 pb-4">
      <div className="flex justify-between">
        <AdminHeader header={ADMIN_PANEL_HEADER.OVERALL_RESPONSE_VALIDATOR} />
        <div className="flex justify-end">
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className="font-medium text-[#005587] flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
            Refresh Data
          </button>
        </div>
      </div>

      <div className="pt-4 pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Button
              onClick={handleReAnalyse}
              variant={'outline'}
              disabled={revalidateMutation.isLoading || analysing}
              className="font-medium text-[#005587] flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed mr-2"
            >
              {revalidateMutation.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCw className="h-4 w-4" />
              )}
              Re Analyse data
            </Button>
            <Popover>
              <PopoverTrigger>
                <Info className="h-4 w-4" />
              </PopoverTrigger>
              <PopoverContent className="font-secondary w-[320px]" align="start">
                <div className="text-sm">Runs DeepEval metrics on the golden Q&A pairs stored in the database using the system's current prompt configuration.</div>
                <hr className="my-2" />
                <div className="text-xs">
                  This process may take some time, during which the button will be disabled. Check back later or refresh the page to view the latest status.
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="text-sm text-gray-500">
            Last analysis: {data.length > 0 && formatDate(data[0].last_run)}
          </div>
        </div>
      </div>

      <div className="mt-2">
        <DataTable
          data={data}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No response data available"
          getRowKey={(row) => `${row.corpus}-${row.model}`}
        />
      </div>

      {data.length > 0 && (
        <div className="mt-6">
          <OverallResponseChart data={data} />
        </div>
      )}
    </div>
  );
};

